import type { Injector } from '@angular/core';
import type { NonNullableFormBuilder } from '@angular/forms';
import { Validators } from '@angular/forms';
import {
  onyxTextareaLengthValidator,
  type OnyxCustomAddress,
} from '@onyx/angular';
import { omit } from 'lodash';
import type { TelematicsItem } from '../../../../../common/interfaces/telematics/telematics-item';
import { TelematicsRoutePointStatus } from '../../../../../common/interfaces/telematics/telematics-vehicle-point-status';
import { SwapPointType } from '../../../../fleet/common/enums/technical-point-type';
import type {
  MapSwapFormDto,
  MapSwapPanelForm,
  MapSwapPanelRouteForm,
  MapSwapPanelRouteFormGroup,
  MapSwapPanelSetForm,
} from '../../../map/map-swap-panel/map-swap-panel.component';
import { SwapSetAction } from '../../enums/swap/swap-set-action';
import type { SwapRoutePoint } from '../../interfaces/swap/swap-route-point';
import type { SwapSet } from '../../interfaces/swap/swap-set';
import type { SwapUnassignedPoint } from '../../interfaces/swap/swap-unassigned-points';
import { SwapStore } from '../../stores/swap.store';
import { SwapHelper } from './swap.helper';

export class SwapFormHelper {
  public static buildForm(fb: NonNullableFormBuilder) {
    return fb.group({
      isDouble: fb.control(false, [Validators.required]),
      address: fb.control<OnyxCustomAddress | null>(null, [
        Validators.required,
      ]),
      serviceTime: fb.control<number | null>(3600, [Validators.required]),
      isRecorded: fb.control(false, [Validators.required]),

      vehicleUuid: fb.control<TelematicsItem | null>(
        { value: null, disabled: true },
        [Validators.required],
      ),
      vehicleRoute: fb.array<MapSwapPanelRouteFormGroup>(
        [],
        [Validators.required],
      ),
      vehicleUnassignedPoints: fb.control<string[]>([]),
      vehicleSemiTrailerPoints: fb.control<string[]>([]),
      vehicleSet: SwapFormHelper.buildSetFormGroup(fb),
      note: fb.control<string | null>(null, [onyxTextareaLengthValidator]),

      otherVehicleUuid: fb.control<TelematicsItem | null>(null, [
        Validators.required,
      ]),
      otherVehicleRoute: fb.array<MapSwapPanelRouteFormGroup>(
        [],
        [Validators.required],
      ),
      otherVehicleUnassignedPoints: fb.control<string[] | null>(null),
      otherVehicleSemiTrailerPoints: fb.control<string[] | null>(null),
      otherVehicleSet: SwapFormHelper.buildSetFormGroup(fb),
      otherNote: fb.control<string | null>(null, [onyxTextareaLengthValidator]),
    });
  }

  public static buildRouteFormGroup(fb: NonNullableFormBuilder) {
    return fb.group({
      orderUuid: fb.control<string | null>(null),
      vehicleUuid: fb.control<string | null>(null),
      pointUuid: fb.control<string | null>(null, [Validators.required]),
      category: fb.control<string | null>(null, [Validators.required]),
    });
  }

  public static buildSetFormGroup(fb: NonNullableFormBuilder) {
    return fb.group({
      trailerUuid: fb.control<string | null>(null),
      primaryDriverUuid: fb.control<string | null>(null),
      secondaryDriverUuid: fb.control<string | null>(null),
    });
  }

  public static buildFormValues(
    address: OnyxCustomAddress | null,
    vehicleData: {
      vehicle: TelematicsItem | null;
      route: SwapRoutePoint[];
      unassigned: SwapUnassignedPoint[];
      trailers: SwapUnassignedPoint[];
      set: SwapSet;
    },
    otherData: {
      otherVehicle: TelematicsItem | null;
      otherRoute: SwapRoutePoint[];
      otherUnassigned: SwapUnassignedPoint[];
      otherTrailers: SwapUnassignedPoint[];
      otherSet: SwapSet;
    },
  ): Partial<MapSwapPanelForm> {
    const { vehicle, route, unassigned, trailers, set } = vehicleData;
    const {
      otherVehicle,
      otherRoute,
      otherUnassigned,
      otherTrailers,
      otherSet,
    } = otherData;

    return {
      address,
      vehicleUuid: vehicle,
      vehicleRoute: SwapFormHelper.getFormRoute(route, vehicle),
      vehicleUnassignedPoints: SwapHelper.getPointsUuid(unassigned),
      vehicleSemiTrailerPoints: SwapHelper.getPointsUuid(trailers),
      vehicleSet: SwapFormHelper.createFormSet(set, otherSet),
      otherVehicleUuid: otherVehicle,
      otherVehicleRoute: SwapFormHelper.getFormRoute(otherRoute, otherVehicle),
      otherVehicleUnassignedPoints: SwapHelper.getPointsUuid(otherUnassigned),
      otherVehicleSemiTrailerPoints: SwapHelper.getPointsUuid(otherTrailers),
      otherVehicleSet: SwapFormHelper.createFormSet(otherSet, set),
    };
  }

  public static getFormRoute(
    route: SwapRoutePoint[],
    vehicle: TelematicsItem | null,
  ) {
    return route.map((item) => ({
      orderUuid: item.isSwapPoint ? null : item.point.order?.uuid,
      vehicleUuid: item.isSwapPoint ? (vehicle?.uuid ?? null) : null,
      pointUuid: SwapHelper.getPointUuid(item),
      category: item.isSwapPoint
        ? item.point.category
        : item.point.point.category,
    }));
  }

  public static createFormSet(
    set: SwapSet,
    otherSet: SwapSet,
  ): MapSwapPanelSetForm {
    const drivers = set.drivers
      .filter(
        ({ value, action }) =>
          value && action !== SwapSetAction.DROP_OFF_IN_POINT,
      )
      .map(({ value }) => value!.uuid);

    const trailerUuid = (() => {
      const trailer = set.trailer;
      const trailerUuid = trailer?.value?.uuid ?? null;

      switch (trailer.action) {
        case SwapSetAction.DROP_OFF_IN_POINT:
          return null;

        case SwapSetAction.TRANSFER: {
          return trailer.initialData?.uuid === trailerUuid &&
            otherSet?.trailer.value?.uuid === trailerUuid
            ? null
            : trailerUuid;
        }

        default:
          return trailerUuid;
      }
    })();

    return {
      trailerUuid,
      primaryDriverUuid: drivers.at(0) ?? null,
      secondaryDriverUuid: drivers.at(1) ?? null,
    };
  }

  public static getDto(
    form: MapSwapPanelForm,
    injector: Injector,
  ): MapSwapFormDto {
    const store = injector.get(SwapStore);

    const vehicle = form.vehicleUuid;
    const otherVehicle = form.otherVehicleUuid;
    const isDouble = form.isDouble;

    const getFilteredRoute = (route: MapSwapPanelRouteForm[]) => {
      const points = [
        ...store.primaryVehicleData().route,
        ...(isDouble ? store.otherVehicleData().route : []),
      ];

      return route.filter((routePoint) => {
        const matchedPoint = points.find(
          (item) => SwapHelper.getPointUuid(item) === routePoint.pointUuid,
        );

        if (!matchedPoint) return false;

        return (
          matchedPoint.isSwapPoint ||
          matchedPoint.point.status === TelematicsRoutePointStatus.ASSIGNED
        );
      });
    };

    const vehicleRoute = getFilteredRoute(form.vehicleRoute);
    const otherVehicleRoute = isDouble
      ? getFilteredRoute(form.otherVehicleRoute)
      : null;

    return {
      ...omit(form, 'isDouble'),
      vehicleUuid: vehicle?.uuid ?? null,
      type: isDouble ? SwapPointType.DOUBLE : SwapPointType.SINGLE,
      vehicleRoute,
      otherVehicleUuid: otherVehicle?.uuid ?? null,
      otherVehicleRoute,
      otherVehicleSet: isDouble ? form.otherVehicleSet : null,
      otherVehicleSemiTrailerPoints: isDouble
        ? form.otherVehicleSemiTrailerPoints
        : null,
      otherVehicleUnassignedPoints: isDouble
        ? form.otherVehicleUnassignedPoints
        : null,
    };
  }
}
